import type { DeviceHandle, DeviceDirection } from './types';

/**
 * Maps a raw API device object to a DeviceHandle.
 *
 * Single source of truth for manufacturer/device_type → vendor mapping.
 * Prefer this over inline ternary chains in components.
 *
 * Priority: manufacturer field first, then device_type fallback.
 */
export function rawDeviceToHandle(
  rawDevice: any,
  direction?: DeviceDirection,
): DeviceHandle {
  const mfr = rawDevice.manufacturer?.toLowerCase() ?? '';

  const vendorByType: Record<string, DeviceHandle['vendor']> = {
    facial_recognition: 'hikvision',
    rfid_reader:        'controlid',  // ControlID iDUHF UHF reader — same API as controlid_rfid
    access_controller:  'controlid',
    controlid_face:     'controlid',
    controlid_rfid:     'controlid',
  };

  // Substring match, not exact equality — ControlID's iDBox/iDFace lines report
  // their manufacturer as "idbox"/"idface", not literally "controlid".
  const isControlIDMfr = mfr.includes('controlid') || mfr.includes('idface') || mfr.includes('idbox');
  const vendor: DeviceHandle['vendor'] =
    isControlIDMfr ? 'controlid' :
    mfr === 'hikvision' ? 'hikvision' :
    vendorByType[rawDevice.device_type] ?? 'hikvision';

  return {
    id:               rawDevice.id,
    name:             rawDevice.name || undefined,
    vendor,
    device_type:      rawDevice.device_type,
    ip_address:       rawDevice.ip_address,
    local_ip_address: rawDevice.local_ip_address,
    port:             rawDevice.port,
    username:         rawDevice.username || 'admin',
    password:         rawDevice.password || '',
    config:           rawDevice.config,
    direction,
  };
}
